import { SubscriptionCadence } from "components/Conversations/Views/Drawer/components/Subscriptions/helpers";
import BaseService from "./BaseService";

export const DEFAULT_KBO_FLOW_WORKFLOW_NAME = "KboFlowWorkflow:v3";

export type KboFlowWorkflowInput = {
    targetQuestionCount: number;
    maxQuestionsToPursue: number;
    ignoreHistory: boolean;
};

export type KboFlowSubscription = {
    id: string;
    userId: string;
    userName?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    cadence: SubscriptionCadence;
    workflowName: string;
    workflowInput?: KboFlowWorkflowInput | null;
    nextRun?: string | null;
    lastRun?: string | null;
    createdOn: string;
    modifiedOn: string;
};

export type CreateKboFlowSubscriptionRequest = {
    userId: string;
    cadence: SubscriptionCadence;
    workflowName: string;
    workflowInput?: KboFlowWorkflowInput | null;
};

export type CreateKboFlowSubscriptionsRequest = {
    userIds: string[];
    cadence: SubscriptionCadence;
};

export type UpdateKboFlowSubscriptionRequest = Omit<
    CreateKboFlowSubscriptionRequest,
    "userId"
>;

export type KboFlowQuestionSubscription = {
    id: string;
    name: string;
    questionText: string;
    cadence: SubscriptionCadence;
    nextRun?: string | null;
    lastRun?: string | null;
    createdOn: string;
    modifiedOn: string;
};

export type KboFlowTenantQuestionSubscription = KboFlowQuestionSubscription & {
    userId: string;
    userName?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
};

export type SaveKboFlowQuestionSubscriptionRequest = {
    name: string;
    questionText: string;
    cadence: SubscriptionCadence;
};

export type RunKboFlowQuestionOnceRequest = {
    questionText: string;
    startDateUtc: string;
    endDateUtc: string;
};

export class KboFlowSubscriptionsService extends BaseService {
    protected urlBase = "api/KboFlowSubscriptions";

    public async list(
        abortSignal?: AbortSignal,
    ): Promise<KboFlowSubscription[]> {
        return this.get<KboFlowSubscription[]>(
            new URLSearchParams(),
            "List",
            abortSignal,
        );
    }

    public async create(
        request: CreateKboFlowSubscriptionRequest,
    ): Promise<KboFlowSubscription> {
        return this.post<KboFlowSubscription>(
            JSON.stringify(request),
            new URLSearchParams(),
            "Create",
            { returnJson: true },
        );
    }

    public async createBatch(
        request: CreateKboFlowSubscriptionsRequest,
    ): Promise<KboFlowSubscription[]> {
        return this.post<KboFlowSubscription[]>(
            JSON.stringify(request),
            new URLSearchParams(),
            "CreateBatch",
            { returnJson: true },
        );
    }

    public async update(
        id: string,
        request: UpdateKboFlowSubscriptionRequest,
    ): Promise<KboFlowSubscription> {
        return this.put<KboFlowSubscription>(
            new URLSearchParams(),
            `Update/${id}`,
            JSON.stringify(request),
        );
    }

    public async remove(id: string): Promise<void> {
        await this.delete(new URLSearchParams(), `Delete/${id}`);
    }

    // --- Self-service (User Settings tab) ---

    public async getMine(): Promise<KboFlowSubscription | null> {
        const res = await this.get<Response>(
            new URLSearchParams(),
            "Me",
            undefined,
            undefined,
            true,
        );
        const text = await res.text();
        return text ? (JSON.parse(text) as KboFlowSubscription) : null;
    }

    public async subscribeMine(
        cadence: SubscriptionCadence,
    ): Promise<KboFlowSubscription> {
        return this.put<KboFlowSubscription>(
            new URLSearchParams(),
            "Subscribe",
            JSON.stringify({ cadence }),
        );
    }

    public async unsubscribeMine(): Promise<void> {
        await this.delete(new URLSearchParams(), "Subscribe");
    }

    // --- Question subscriptions ("My subscriptions" tab, internal users only) ---

    /** Every user's question subscriptions in the tenant (read-only). */
    public async listQuestions(
        abortSignal?: AbortSignal,
    ): Promise<KboFlowTenantQuestionSubscription[]> {
        return this.get<KboFlowTenantQuestionSubscription[]>(
            new URLSearchParams(),
            "Questions/List",
            abortSignal,
        );
    }

    public async listMyQuestions(
        abortSignal?: AbortSignal,
    ): Promise<KboFlowQuestionSubscription[]> {
        return this.get<KboFlowQuestionSubscription[]>(
            new URLSearchParams(),
            "Questions/Mine",
            abortSignal,
        );
    }

    public async createQuestion(
        request: SaveKboFlowQuestionSubscriptionRequest,
    ): Promise<KboFlowQuestionSubscription> {
        return this.post<KboFlowQuestionSubscription>(
            JSON.stringify(request),
            new URLSearchParams(),
            "Questions",
            { returnJson: true },
        );
    }

    public async updateQuestion(
        id: string,
        request: SaveKboFlowQuestionSubscriptionRequest,
    ): Promise<KboFlowQuestionSubscription> {
        return this.put<KboFlowQuestionSubscription>(
            new URLSearchParams(),
            `Questions/${id}`,
            JSON.stringify(request),
        );
    }

    public async removeQuestion(id: string): Promise<void> {
        await this.delete(new URLSearchParams(), `Questions/${id}`);
    }

    /** Ultra users only: runs a question once now, with no subscription or email. */
    public async runQuestionOnce(
        request: RunKboFlowQuestionOnceRequest,
    ): Promise<{ correlationId: string }> {
        return this.post<{ correlationId: string }>(
            JSON.stringify(request),
            new URLSearchParams(),
            "Questions/RunOnce",
            { returnJson: true },
        );
    }
}
